/**
 * Calendar keyboard shortcuts (keymap entries `calendar.*` in packages/ui use-shortcut.ts): active on the calendar
 * page when no text field, menu or dialog has the keys. "?" lists them; G opens "Go to date".
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { Button, DateTimePicker, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, getShortcuts, isTypingTarget, Kbd, Label, matchesChord, type ShortcutAction } from '@platform/ui';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

export const CALENDAR_SHORTCUTS = [
  'calendar.new',
  'calendar.quickAdd',
  'calendar.today',
  'calendar.next',
  'calendar.previous',
  'calendar.viewDay',
  'calendar.viewWorkWeek',
  'calendar.viewWeek',
  'calendar.viewMonth',
  'calendar.viewAgenda',
  'calendar.goTo',
  'calendar.search',
  'calendar.open',
  'calendar.delete',
  'calendar.copy',
  'calendar.paste',
  'calendar.duplicate',
  'calendar.print',
  'calendar.help',
] as const satisfies readonly ShortcutAction[];
export type CalendarShortcut = (typeof CALENDAR_SHORTCUTS)[number];

/** Handler per shortcut; returning false leaves the key to the browser (e.g. Ctrl+C on selected text). */
export type CalendarShortcutHandlers = Partial<Record<CalendarShortcut, () => boolean | void>>;

const blocked = () => document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]') !== null;

export function useCalendarShortcuts(handlers: CalendarShortcutHandlers): void {
  const current = useRef(handlers);
  useEffect(() => {
    current.current = handlers;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTypingTarget(event.target) || blocked()) return;
      // Buttons and links keep Enter/Space for themselves.
      if (event.key === 'Enter' && event.target instanceof Element && event.target.closest('button, a')) return;
      for (const action of CALENDAR_SHORTCUTS) {
        if (!getShortcuts(action).some((chord) => matchesChord(event, chord))) continue;
        const handler = current.current[action];
        if (!handler) return;
        if (handler() === false) return;
        event.preventDefault();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

/** "Ctrl+D" / "⌘D" style text of every chord of a shortcut. */
function chords(action: CalendarShortcut): string[] {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return getShortcuts(action).map((chord) =>
    chord
      .split('+')
      .map((part) => (part === 'Mod' ? (mac ? '⌘' : 'Ctrl') : part === 'ArrowLeft' ? '←' : part === 'ArrowRight' ? '→' : part))
      .filter((part, _index, all) => !(part === 'Shift' && all.at(-1) === '?'))
      .join('+'),
  );
}

export function ShortcutHelpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.shortcuts.title')}</DialogTitle>
          <DialogDescription>{t('webmail.calendarUx.shortcuts.description')}</DialogDescription>
        </DialogHeader>
        <dl className="flex flex-col gap-1.5 text-sm">
          {CALENDAR_SHORTCUTS.map((action) => (
            <div key={action} className="flex items-center justify-between gap-6">
              <dt>{t(`webmail.calendarUx.shortcuts.${action.slice('calendar.'.length)}` as MessageKey)}</dt>
              <dd className="flex justify-end gap-1">
                {chords(action).map((chord) => (
                  <Kbd key={chord}>{chord}</Kbd>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}

export function GoToDateDialog({ open, initial, onOpenChange, onGo }: { open: boolean; initial: Date; onOpenChange: (open: boolean) => void; onGo: (date: Date) => void }) {
  const { t } = useI18n();
  const id = useId();
  const pad = (n: number) => String(n).padStart(2, '0');
  const [value, setValue] = useState(`${initial.getFullYear()}-${pad(initial.getMonth() + 1)}-${pad(initial.getDate())}`);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return;
    onGo(date);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.shortcuts.goTo')}</DialogTitle>
          <DialogDescription>{t('webmail.calendarUx.goTo.description')}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <Label htmlFor={id}>{t('webmail.calendarUx.goTo.date')}</Label>
          <DateTimePicker id={id} mode="date" value={value} autoFocus onValueChange={setValue} />
          <DialogFooter>
            <Button type="submit">{t('webmail.calendarUx.goTo.go')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
